import React, {
  FunctionComponent,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import moment, { Moment } from 'moment';
import styled from 'styled-components';

import { SubmitButton, ScreenReaderUpdateText } from 'components';
import { FormGroup, DatePicker } from 'components/chroma';
import { TextSmall } from 'components/chroma/extended';

import {
  toAussieStandardDateFormat,
  toDayOfMonthWithOrdinal,
} from 'scenes/common/helpers/datetime';
import { SegmentedControlQuestion } from 'scenes/common/components/Questions';
import {
  LabelValueType,
  PaymentFrequency,
  SingleFieldStepProps,
} from 'scenes/common/types';
import {
  toCurrencyWithSign,
  TWO_DECIMAL_PLACES,
} from 'scenes/common/helpers/currency';

const PAYMENT_DATE = 'paymentDate';
export const QUESTION = 'When would you like to pay your premium?';
const MAXIMUM_FUTURE_DAYS = 14;

const isOutsideRange = (date: Moment, policyStartDate: Moment): boolean =>
  date.isBefore(policyStartDate) ||
  date.isAfter(policyStartDate.add(MAXIMUM_FUTURE_DAYS, 'day'));

const isValidDate = (date: string, policyStartDate: string): boolean =>
  Boolean(date) && !isOutsideRange(moment(date), moment(policyStartDate));

const DatePickerWrapper = styled.div`
  margin-top: 0.7rem;
`;

const FootnoteWrapper = styled.div`
  margin-top: -0.5rem;
  margin-bottom: 1.5rem;
`;

const PaymentDateStep: FunctionComponent<
  Pick<SingleFieldStepProps<string>, 'onNext' | 'defaultStateValue'> & {
    policyStartDate: string;
    paymentDateOptions: LabelValueType[];
    paymentFrequency: PaymentFrequency;
    onUpdatePaymentDate: (paymentDate: string) => void;
    firstInstalment: string;
    monthlyInstalment: string;
  }
> = ({
  policyStartDate,
  defaultStateValue,
  paymentDateOptions: paymentOptions,
  paymentFrequency,
  firstInstalment,
  monthlyInstalment,

  onNext,
  onUpdatePaymentDate,
}) => {
  const {
    formState: { isValid },
    handleSubmit,
    control,
    errors,
  } = useForm({
    mode: 'onChange',
  });

  const [validPaymentDate, setValidPaymentDate] = useState(defaultStateValue);

  useEffect(() => {
    if (defaultStateValue !== policyStartDate) {
      onUpdatePaymentDate(defaultStateValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback(
    (value: string, onChange: Function): void => {
      onChange(value);
      if (isValidDate(value, policyStartDate)) {
        setValidPaymentDate(value);
        onUpdatePaymentDate(value);
      }
    },
    [onUpdatePaymentDate, policyStartDate]
  );

  return (
    <form onSubmit={handleSubmit(onNext)}>
      {paymentFrequency === PaymentFrequency.Annually ? (
        <SegmentedControlQuestion
          errors={errors}
          control={control}
          stateKey={PAYMENT_DATE}
          options={paymentOptions}
          defaultValue={defaultStateValue}
          question={QUESTION}
          stack
          validationRules={{
            validate: {
              oneOf: (value) =>
                paymentOptions.some((option) => option.value === value),
            },
          }}
        />
      ) : (
        <>
          <ScreenReaderUpdateText />
          <Controller
            control={control}
            name={PAYMENT_DATE}
            defaultValue={defaultStateValue}
            rules={{
              validate: {
                validDate: (value) =>
                  isValidDate(value, policyStartDate) || 'Invalid date',
              },
            }}
            render={({ onChange, value }) => (
              <FormGroup
                id={PAYMENT_DATE}
                label={QUESTION}
                error={errors[PAYMENT_DATE]?.message || ''}
              >
                <DatePickerWrapper>
                  <DatePicker
                    id={PAYMENT_DATE}
                    screenReaderInputMessage="Choose my own payment date"
                    displayFormat="DD/MM/YYYY"
                    isOutsideRange={(date: Moment) =>
                      isOutsideRange(date, moment(policyStartDate))
                    }
                    numberOfMonths={1}
                    value={value}
                    onChange={(val: string) => {
                      handleChange(val, onChange);
                    }}
                  />
                </DatePickerWrapper>
              </FormGroup>
            )}
          />
        </>
      )}

      {paymentFrequency === PaymentFrequency.Monthly && (
        <FootnoteWrapper>
          <TextSmall>
            This is the date each month that your monthly instalments will be
            debited from your nominated account. You can choose to pay anytime
            within the next 14 days from the{' '}
            <strong>
              {toAussieStandardDateFormat(policyStartDate, 'YYYY-MM-DD')}
            </strong>
            .
          </TextSmall>
          <TextSmall>
            We will debit the first instalment of&nbsp;
            <strong>
              {toCurrencyWithSign(firstInstalment, TWO_DECIMAL_PLACES)}
            </strong>{' '}
            from your nominated account within 3 business days of your selected
            billing date{' '}
            {toAussieStandardDateFormat(validPaymentDate, 'YYYY-MM-DD')}. All
            future instalments of{' '}
            <strong>
              {toCurrencyWithSign(monthlyInstalment, TWO_DECIMAL_PLACES)}
            </strong>{' '}
            will be debited on the {toDayOfMonthWithOrdinal(validPaymentDate)}{' '}
            day of each month.
          </TextSmall>
          <TextSmall>
            Your Renewal invitation will also auto renew and continue on monthly
            instalments unless you contact us to change this to an annual
            payment.
          </TextSmall>
        </FootnoteWrapper>
      )}

      <SubmitButton enabled={isValid}>Proceed to Payment</SubmitButton>
    </form>
  );
};

export default PaymentDateStep;
